import { useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { useSceneLineMarker } from '@editor/hooks'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { WidthAnchorMode } from '../FormFields/utils'

const OpeningFormSceneMarkers = (): null => {
  const globalStart = useWatch({ name: 'globalStart' }) as
    | { x: number; y: number; z: number }
    | undefined
  const globalEnd = useWatch({ name: 'globalEnd' }) as
    | { x: number; y: number; z: number }
    | undefined
  const openingWidthAnchorMode =
    (useWatch({ name: 'openingWidthAnchorMode' }) as WidthAnchorMode | undefined) ?? 'start'

  const { rectangleLines, orthogonalLineAtP1, orthogonalLineAtP2 } = useMemo(() => {
    const newP1 = new ImmutableVector3(globalStart?.x, globalStart?.y, globalStart?.z)
    const newP2 = new ImmutableVector3(globalEnd?.x, globalEnd?.y, globalStart?.z)
    const newP3 = new ImmutableVector3(globalEnd?.x, globalEnd?.y, globalEnd?.z)
    const newP4 = new ImmutableVector3(globalStart?.x, globalStart?.y, globalEnd?.z)

    const rectangleLines = [
      [newP1, newP2],
      [newP2, newP3],
      [newP3, newP4],
      [newP4, newP1],
    ]

    const openingNormal = newP2.sub(newP1).cross(newP4.sub(newP1)).normalize()
    const halfOrthogonalLength = 0.1

    const orthogonalLineAtP1 = [
      newP1.addScaledVector(openingNormal, -halfOrthogonalLength),
      newP1.addScaledVector(openingNormal, halfOrthogonalLength),
    ]
    const orthogonalLineAtP2 = [
      newP2.addScaledVector(openingNormal, -halfOrthogonalLength),
      newP2.addScaledVector(openingNormal, halfOrthogonalLength),
    ]

    return { rectangleLines, orthogonalLineAtP1, orthogonalLineAtP2 }
  }, [globalStart, globalEnd])

  const startEnabled =
    typeof globalStart?.x === 'number' &&
    typeof globalStart?.y === 'number' &&
    typeof globalStart?.z === 'number'

  const endEnabled =
    typeof globalEnd?.x === 'number' &&
    typeof globalEnd?.y === 'number' &&
    typeof globalEnd?.z === 'number'

  const highlightedLineIndexes = useMemo(() => {
    if (openingWidthAnchorMode === 'start') return [1]
    if (openingWidthAnchorMode === 'end') return [3]
    return [1, 3]
  }, [openingWidthAnchorMode])

  const orthogonalHighlightedLines = useMemo(() => {
    if (openingWidthAnchorMode === 'start') return [orthogonalLineAtP2]
    if (openingWidthAnchorMode === 'end') return [orthogonalLineAtP1]
    return [orthogonalLineAtP1, orthogonalLineAtP2]
  }, [openingWidthAnchorMode, orthogonalLineAtP1, orthogonalLineAtP2])

  const orthogonalDefaultLines = useMemo(() => {
    if (openingWidthAnchorMode === 'start') return [orthogonalLineAtP1]
    if (openingWidthAnchorMode === 'end') return [orthogonalLineAtP2]
    return []
  }, [openingWidthAnchorMode, orthogonalLineAtP1, orthogonalLineAtP2])

  const defaultLines = useMemo(
    () => [
      ...rectangleLines.filter((_line, index) => !highlightedLineIndexes.includes(index)),
      ...orthogonalDefaultLines,
    ],
    [highlightedLineIndexes, orthogonalDefaultLines, rectangleLines],
  )

  const highlightedLines = useMemo(
    () => [
      ...rectangleLines.filter((_line, index) => highlightedLineIndexes.includes(index)),
      ...orthogonalHighlightedLines,
    ],
    [highlightedLineIndexes, orthogonalHighlightedLines, rectangleLines],
  )

  useSceneLineMarker({
    lines: defaultLines,
    enabled: startEnabled && endEnabled,
    color: 'black',
    lineWidth: 5,
    renderOrder: 999,
    disableDepthTest: true,
  })

  useSceneLineMarker({
    lines: highlightedLines,
    enabled: startEnabled && endEnabled,
    color: 'blue',
    lineWidth: 5,
    renderOrder: 1000,
    disableDepthTest: true,
  })

  return null
}

export default OpeningFormSceneMarkers
